import * as ort from 'onnxruntime-web';
import { configureOnnxWasmRuntime, getExecutionProvider } from '../utils/aiRuntime';

const sessionCache = new Map();

function buildCacheKey(modelSource, device) {
  if (typeof modelSource === 'string') {
    return `${device}:path:${modelSource}`;
  }
  if (modelSource instanceof ArrayBuffer) {
    return `${device}:buffer:${modelSource.byteLength}`;
  }
  throw new Error('Invalid model source. Expected a path string or ArrayBuffer.');
}

function buildSessionOptions(device) {
  configureOnnxWasmRuntime();
  return {
    executionProviders: getExecutionProvider(device),
    graphOptimizationLevel: 'disabled',
    enableCpuMemArena: true,
    enableMemPattern: true,
    logSeverityLevel: 3, // 0=Verbose, 1=Info, 2=Warning, 3=Error, 4=Fatal
    logVerbosityLevel: 3, // 0=Verbose, 1=Info, 2=Warning, 3=Error, 4=Fatal
  };
}


/**
 * Get or create an ONNX Runtime session for a model key.
 * @param {string} [modelKey] - Optional. If omitted, cache key is derived from modelSource and device.
 * @param {string|ArrayBuffer} modelSource - Model URL path or ArrayBuffer.
 * @param {'webgpu'|'wasm'} [device='wasm']
 * @returns {Promise<ort.InferenceSession>}
 */
export async function getSession(modelKey, modelSource, device = 'wasm') {
  const cacheKey = buildCacheKey(modelSource, device);
  const key = typeof modelKey === 'string' && modelKey ? modelKey : cacheKey;
  const cached = sessionCache.get(key);
  if (cached && cached.cacheKey === cacheKey) {
    return cached.session;
  }

  const options = buildSessionOptions(device);
  let session;
  session = await ort.InferenceSession.create(modelSource, options);
  sessionCache.set(key, { cacheKey, session });
  return session;
}

export function clearSession(modelKey) {
  sessionCache.delete(modelKey);
}

